// Remembering where someone was headed while they authenticate.
//
// A visitor who follows a deep link into a protected page gets bounced to
// /login by ProtectedRoute. Without this, login sends everyone to its own
// default landing page (/tg-product-assistant) and the link they clicked is simply lost.
// The trend-report newsletter's "Read on PIMS" button is the case that made
// this visible - it points straight at /trend-reports/<date>, and most people
// open it in a browser that is not signed in.
//
// The destination travels as a query parameter rather than as router state
// because it has to survive things state does not: a page reload, a pasted or
// bookmarked URL, and the hop through /verify-email or /signup that a
// first-time recipient goes through before they ever reach a dashboard.

export const REDIRECT_PARAM = "redirect";

// Where the auth pages send someone when nothing better was remembered.
export const DEFAULT_LANDING = "/tg-product-assistant";

/**
 * Read the remembered destination out of a location's search string, or "" if
 * there isn't a usable one.
 *
 * The same-site check is the substance of this function, not a formality: the
 * value arrives from the URL bar, so accepting it as-is would turn our own
 * login form into an open redirect - hand someone
 * `/login?redirect=https://evil.example` and the phishing page inherits our
 * domain's credibility.
 *
 * "//host" and "/\host" are rejected next to the obvious "https://host":
 * browsers read a leading double slash as protocol-relative, which is an
 * off-site jump wearing a relative path's clothes.
 */
export function readRedirect(search) {
    const value = new URLSearchParams(search).get(REDIRECT_PARAM) || "";
    if (!value.startsWith("/")) return "";
    if (value.startsWith("//") || value.startsWith("/\\")) return "";
    return value;
}

/**
 * Build the search string for an auth page (`?lang=…`), carrying the
 * destination along when there is one worth carrying.
 *
 * `from` is a "path?query" string - what ProtectedRoute was guarding, or what
 * a previous auth page had already remembered, so the value forwards down a
 * chain of hops without each page having to know the shape of it.
 */
export function authSearch(language, from) {
    const params = new URLSearchParams({ lang: language });
    // DEFAULT_LANDING and "/" are where these pages go anyway, so remembering
    // either only adds noise to the URL.
    if (from && from !== "/" && !from.startsWith(DEFAULT_LANDING)) {
        params.set(REDIRECT_PARAM, from);
    }
    return `?${params.toString()}`;
}

/**
 * Where to send someone now that they are authenticated: the destination they
 * were originally after, or `fallback`.
 *
 * The active language is copied onto the target when the target does not name
 * one itself, so someone who switched the login form to 中文 does not land on
 * an English page.
 */
export function redirectTarget(search, language, fallback = DEFAULT_LANDING) {
    const [pathname, query = ""] = (readRedirect(search) || fallback).split("?");
    const params = new URLSearchParams(query);
    if (!params.get("lang")) params.set("lang", language);
    return { pathname, search: `?${params.toString()}` };
}
